// ── Trust Score Hook ──────────────────────────────────────────────
//
// DESIGN PHILOSOPHY:
// Rate limiting should ONLY restrict users we can PROVE are new.
// If we can't confirm someone is new, they post freely.
// False negatives (spammer slips through) are acceptable.
// False positives (legitimate user blocked) are NOT acceptable.

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNostrQuery, useNostrProfile } from './useNostrQuery';
import { getLocalEvents } from './useNostrPublish';
import { PLEDGE_KIND } from '../lib/crowdfund';
import {
  calculateTrustScore,
  getTrustLevel,
  getRawTrustLevel,
  remainingPostsToday,
  accountAgeDays,
  isNewbie,
  isRestricted,
  isEstablishedAge,
  isOperator,
  ACCOUNT_AGE,
  UNVERIFIED_LEVEL_CAP,
  type TrustLevel,
  type TrustSignals,
} from '../lib/trustScore';
import { useNip05Verified } from './useNip05Verified';

// ── Helpers ─────────────────────────────────────────────────────

function isReplyEvent(event: any): boolean {
  if (!event.tags) return false;
  return event.tags.some((t: string[]) => t[0] === 'e');
}

function getMergedTodayCount(pubkey: string, relayPosts: any[]): number {
  const cutoff = Math.floor(Date.now() / 1000) - 86400;
  const seen = new Set<string>();

  for (const e of relayPosts) {
    if (e.pubkey === pubkey && e.created_at >= cutoff && !isReplyEvent(e)) {
      seen.add(e.id);
    }
  }

  const local = getLocalEvents();
  for (const e of local) {
    if (e.pubkey === pubkey &&
        (e.kind === 1 || e.kind === 1068) &&
        e.created_at >= cutoff &&
        !isReplyEvent(e) &&
        !seen.has(e.id)) {
      seen.add(e.id);
    }
  }

  return seen.size;
}

/**
 * Check if ANY event in the given arrays is older than a threshold.
 * If we find even ONE old event, the user is established.
 */
function hasEventOlderThanDays(days: number, ...eventArrays: any[][]): boolean {
  const cutoff = Math.floor(Date.now() / 1000) - days * 86400;
  for (const arr of eventArrays) {
    for (const e of arr) {
      if (e.created_at && e.created_at < cutoff) return true;
    }
  }
  return false;
}

function findOldestTimestamp(...eventArrays: any[][]): number {
  let oldest = Infinity;
  for (const arr of eventArrays) {
    for (const e of arr) {
      if (e.created_at && e.created_at < oldest) {
        oldest = e.created_at;
      }
    }
  }
  return oldest === Infinity ? 0 : oldest;
}

/**
 * Read the persisted trust high-water mark WITHOUT subscribing to it.
 * A non-zero stored level means we have, at some earlier point, confirmed
 * this account is past Level 0 — i.e. it is NOT a brand-new account. Used as
 * an additional "established" signal for rate limiting so a legitimate user
 * is never blocked just because their post history hasn't loaded yet.
 */
function readPersistedTrustLevel(pubkey: string): number {
  try {
    const hwmRaw = localStorage.getItem(`bchnostr:trust-hwm:${pubkey}`);
    if (hwmRaw) {
      const parsed = JSON.parse(hwmRaw);
      // The high-water mark intentionally has no short expiry for this purpose —
      // once established, an account does not become "new" again.
      if (parsed && typeof parsed.level === 'number' && parsed.level > 0) return parsed.level;
    }
  } catch {}
  try {
    const cacheRaw = localStorage.getItem(`bchnostr:trust:${pubkey}`);
    if (cacheRaw) {
      const parsed = JSON.parse(cacheRaw);
      if (parsed && typeof parsed.level === 'number' && parsed.level > 0) return parsed.level;
    }
  } catch {}
  return 0;
}


// ══════════════════════════════════════════════════════════════════
//  useAccountMaturity — lightweight hook for Composer rate limiting
// ══════════════════════════════════════════════════════════════════
//
// This is the ONLY hook that gates posting. It must be bulletproof.
//
// Strategy:
//   1. Query the user's recent posts (last 30 days, limit 50).
//      Relays return the NEWEST matching events (NIP-01 spec).
//   2. If ANY returned post is older than 7 days → user is established.
//      No restrictions applied. Done.
//   3. If all returned posts are within 7 days AND the query returned
//      fewer than 50 events → user is likely actually new. Apply limits.
//   4. If query returned 50 events (hit the limit) and none are > 7 days
//      old → the user has 50+ posts in 7 days — likely not new, just
//      very active. Treat as established to avoid false positives.
//   5. While relay data hasn't arrived yet → UNRESTRICTED.

export function useAccountMaturity(pubkey: string | undefined): {
  ageDays: number;
  firstSeen: number;
  isNewbie: boolean;
  isRestricted: boolean;
  isEstablished: boolean;
  postsRemainingToday: number;
  loading: boolean;
  isOperator: boolean;
} {
  const isOp = pubkey ? isOperator(pubkey) : false;

  // ── Verification: a verified (@bchnostr.com NIP-05) account is never new ──
  // Verification costs real BCH and commits a NIP-05 identity — a far higher
  // bar than any post-history heuristic. Treating verified users as
  // established mirrors canPurchaseBoost() and guarantees a paid, verified
  // member is never shown a "posting limit reached" false positive just
  // because their relay history hasn't loaded.
  const { profile } = useNostrProfile(pubkey || '');
  const nip05Value = typeof profile?.nip05 === 'string' ? profile.nip05 : '';
  const isVerified = useNip05Verified(nip05Value, pubkey);

  // ── Prior-established signal: a persisted non-zero trust level proves we
  // have, at some point, confirmed this account is past Level 0. An account
  // that was established before cannot regress to "new". ──
  const priorTrustLevel = pubkey && !isOp ? readPersistedTrustLevel(pubkey) : 0;

  // Single query: recent posts. Relays return newest first.
  // If any of these is older than 7 days, the user is established.
  const [recentSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 60);
  const { events: recentPosts } = useNostrQuery({
    filters: pubkey ? [{ kinds: [1, 1068], authors: [pubkey], since: recentSince, limit: 50 }] : [],
    enabled: !!pubkey && !isOp,
  });

  // Today's posts for rate-limit count
  const [todaySince] = useState(() => Math.floor(Date.now() / 1000) - 86400);
  const { events: todayPosts } = useNostrQuery({
    filters: pubkey ? [{ kinds: [1, 1068], authors: [pubkey], since: todaySince, limit: 20 }] : [],
    enabled: !!pubkey && !isOp,
  });

  // Poll local events to catch same-session publishes
  const [localPublishCounter, setLocalPublishCounter] = useState(0);
  useEffect(() => {
    if (!pubkey || isOp) return;
    const interval = setInterval(() => setLocalPublishCounter(c => c + 1), 2000);
    return () => clearInterval(interval);
  }, [pubkey, isOp]);

  // ── Determine if user is established ─────────────────────────
  // An account is "established" (no rate limits) if:
  //   - They have ANY post older than 7 days, OR
  //   - They have 50+ posts in the query window (very active), OR
  //   - We haven't received relay data yet (innocent until proven new)
  const established = useMemo(() => {
    if (isOp) return true;
    // Verified accounts are never rate-limited — verification is a stronger
    // anti-spam gate than any post-history heuristic.
    if (isVerified) return true;
    // Previously confirmed past Level 0 → not a new account.
    if (priorTrustLevel > 0) return true;
    if (recentPosts.length === 0) return true; // no data yet = unrestricted
    // If any post is older than 7 days → established
    if (hasEventOlderThanDays(ACCOUNT_AGE.restrictedDays, recentPosts)) return true;
    // If we hit the query limit and ALL are recent, the user is very active → established
    if (recentPosts.length >= 50) return true;
    return false;
  }, [isOp, isVerified, priorTrustLevel, recentPosts.length, recentPosts]);

  // For new accounts: find their oldest post to determine actual age
  const firstSeen = useMemo(() => {
    if (isOp) return 1;
    if (established) return 1; // doesn't matter, they're unrestricted
    const oldest = findOldestTimestamp(recentPosts);
    return oldest > 0 ? oldest : Math.floor(Date.now() / 1000);
  }, [isOp, established, recentPosts.length]);

  const mergedTodayCount = useMemo(() => {
    if (!pubkey || isOp || established) return 0;
    return getMergedTodayCount(pubkey, todayPosts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubkey, isOp, established, todayPosts.length, localPublishCounter]);

  if (isOp) {
    return {
      ageDays: 999, firstSeen: 1, isNewbie: false, isRestricted: false,
      isEstablished: true, postsRemainingToday: Infinity, loading: false, isOperator: true,
    };
  }

  if (established) {
    return {
      ageDays: accountAgeDays(firstSeen),
      firstSeen,
      isNewbie: false,
      isRestricted: false,
      isEstablished: true,
      postsRemainingToday: Infinity,
      loading: false,
      isOperator: false,
    };
  }

  // ── Confirmed new account: apply limits ───────────────────────
  const remaining = remainingPostsToday(firstSeen,
    Array(mergedTodayCount).fill(Math.floor(Date.now() / 1000)));

  return {
    ageDays: accountAgeDays(firstSeen),
    firstSeen,
    isNewbie: isNewbie(firstSeen),
    isRestricted: isRestricted(firstSeen),
    isEstablished: false,
    postsRemainingToday: remaining,
    loading: false,
    isOperator: false,
  };
}


// ══════════════════════════════════════════════════════════════════
//  useTrustScore — full trust score for boost eligibility etc.
// ══════════════════════════════════════════════════════════════════

export interface TrustScoreResult {
  score: number;
  level: TrustLevel;
  /** The uncapped trust level (what the user would be without the verification cap). */
  rawLevel: TrustLevel;
  ageDays: number;
  firstSeen: number;
  isVerified: boolean;
  /** True when the user's level is being capped due to lack of verification. */
  isCapped: boolean;
  postsRemainingToday: number;
  loading: boolean;
  isOperatorAccount: boolean;
  /** Exposed signal breakdown for the reputation card (only populated for non-cached results) */
  signals?: TrustSignals;
}

// In-memory cache
const _cache = new Map<string, {
  score: number;
  level: TrustLevel;
  rawLevel: TrustLevel;
  firstSeen: number;
  signals: TrustSignals;
  timestamp: number;
  v: number;
}>();

// Trust scores change slowly (account age, post count over 60 days).
// A 30-minute TTL is plenty — reduces relay chatter and prevents the
// "partial relay data → score drops to 0 → bounces back" yo-yo effect
// that occurred with the previous 5-minute TTL.
const CACHE_TTL_MS = 30 * 60 * 1000;
const CACHE_VERSION = 5;

// ── High-water mark persistence ───────────────────────────────────
// The high-water mark prevents trust level from dropping during a session
// due to partial relay data. We persist it in localStorage so it survives
// page reloads and component remounts. It's keyed by pubkey and has a
// longer TTL (24h) because trust should only go down over very long periods.
const HWM_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function getHighWaterMark(pubkey: string): { level: TrustLevel; score: number } {
  try {
    const raw = localStorage.getItem(`bchnostr:trust-hwm:${pubkey}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Date.now() - parsed.ts < HWM_TTL_MS) {
        return { level: parsed.level, score: parsed.score };
      }
    }
  } catch {}
  return { level: 0, score: 0 };
}

function saveHighWaterMark(pubkey: string, level: TrustLevel, score: number) {
  try {
    localStorage.setItem(`bchnostr:trust-hwm:${pubkey}`, JSON.stringify({
      level, score, ts: Date.now(),
    }));
  } catch {}
}

function getCached(pubkey: string) {
  const entry = _cache.get(pubkey);
  if (entry && Date.now() - entry.timestamp < CACHE_TTL_MS && entry.v === CACHE_VERSION) return entry;
  try {
    const raw = localStorage.getItem(`bchnostr:trust:${pubkey}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Date.now() - parsed.timestamp < CACHE_TTL_MS && parsed.v === CACHE_VERSION) {
        _cache.set(pubkey, parsed);
        return parsed;
      }
    }
  } catch {}
  return null;
}

/** Read the cached trust data even if it's expired. Used as a fallback
 *  while relay data loads to avoid flashing "New User" temporarily.
 *  Accepts any version >= 4 since the data format hasn't changed. */
function getStaleCache(pubkey: string) {
  const entry = _cache.get(pubkey);
  if (entry && entry.v >= 4) return entry;
  try {
    const raw = localStorage.getItem(`bchnostr:trust:${pubkey}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.v >= 4 && parsed.score !== undefined) {
        return parsed;
      }
    }
  } catch {}
  return null;
}

function setCache(pubkey: string, data: {
  score: number; level: TrustLevel; rawLevel: TrustLevel; firstSeen: number; signals: TrustSignals;
}) {
  const entry = { ...data, timestamp: Date.now(), v: CACHE_VERSION };
  _cache.set(pubkey, entry);
  try {
    localStorage.setItem(`bchnostr:trust:${pubkey}`, JSON.stringify(entry));
  } catch {}
}

export function useTrustScore(pubkey: string | undefined): TrustScoreResult {
  const defaultResult: TrustScoreResult = {
    score: 0, level: 0, rawLevel: 0, ageDays: 0, firstSeen: 0,
    isVerified: false, isCapped: false, postsRemainingToday: Infinity,
    loading: true, isOperatorAccount: false,
  };

  const isOp = pubkey ? isOperator(pubkey) : false;
  const rawCached = pubkey ? getCached(pubkey) : null;

  // ── All hooks called unconditionally (React rules of hooks) ────
  const { profile } = useNostrProfile(pubkey || '');
  const nip05Value = typeof profile?.nip05 === 'string' ? profile.nip05 : '';
  const nip05Verified = useNip05Verified(nip05Value, pubkey);

  // If the user is now verified but the cache says they're not, invalidate
  // the cache so we recalculate the trust score with the correct isVerified.
  // This prevents stale cache from blocking verified users (e.g. from boosting).
  const cached = (rawCached && nip05Verified && !rawCached.signals.isVerified) ? null : rawCached;

  // Run queries if we don't have cached signals.
  // Operators still need signal data for the reputation card UI,
  // even though their trust level is always max.
  const needsQueries = !!pubkey && !cached;

  const [postsSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 60);

  const { events: userPosts } = useNostrQuery({
    filters: pubkey ? [{ kinds: [1], authors: [pubkey], since: postsSince, limit: 200 }] : [],
    enabled: needsQueries,
  });

  const { events: tipsSent } = useNostrQuery({
    filters: pubkey ? [{ kinds: [9735], authors: [pubkey], since: postsSince, limit: 100 }] : [],
    enabled: needsQueries,
  });

  const { events: tipsReceived } = useNostrQuery({
    filters: pubkey ? [{ kinds: [9735], '#p': [pubkey], since: postsSince, limit: 100 }] : [],
    enabled: needsQueries,
  });

  const { events: pledges } = useNostrQuery({
    filters: pubkey ? [{ kinds: [PLEDGE_KIND], authors: [pubkey], limit: 50 }] : [],
    enabled: needsQueries,
  });

  const [result, setResult] = useState<TrustScoreResult>(() => {
    if (!pubkey) return defaultResult;
    if (isOp) return { ...defaultResult, score: 999, level: 3 as TrustLevel, rawLevel: 3 as TrustLevel, ageDays: 999, isVerified: true, isCapped: false, postsRemainingToday: Infinity, isOperatorAccount: true };
    if (cached) {
      const effectiveLevel = cached.level;
      const effectiveRawLevel = cached.rawLevel;
      return {
        score: cached.score,
        level: effectiveLevel,
        rawLevel: effectiveRawLevel,
        ageDays: accountAgeDays(cached.firstSeen),
        firstSeen: cached.firstSeen,
        isVerified: cached.signals.isVerified,
        isCapped: effectiveLevel < effectiveRawLevel,
        postsRemainingToday: Infinity,
        loading: false,
        isOperatorAccount: false,
        signals: cached.signals,
      };
    }
    // ── No fresh cache: use stale cache or high-water mark as fallback ──
    // This prevents flashing "New User" when the cache expires and relay
    // data hasn't arrived yet. The stale data is close enough to show while
    // we wait for fresh data.
    const stale = getStaleCache(pubkey);
    if (stale) {
      return {
        score: stale.score,
        level: stale.level as TrustLevel,
        rawLevel: stale.rawLevel as TrustLevel,
        ageDays: accountAgeDays(stale.firstSeen),
        firstSeen: stale.firstSeen,
        isVerified: stale.signals.isVerified,
        isCapped: stale.level < stale.rawLevel,
        postsRemainingToday: Infinity,
        loading: true, // still loading fresh data
        isOperatorAccount: false,
        signals: stale.signals,
      };
    }
    // No cache at all — use high-water mark to at least show the right level.
    // Read from persisted storage directly here (before the ref is initialized)
    // to avoid a Temporal Dead Zone error with highWaterRef.
    if (pubkey) {
      const hwm = getHighWaterMark(pubkey);
      const hwStale = getStaleCache(pubkey);
      const hwLevel = Math.max(hwm.level, hwStale ? hwStale.level : 0);
      const hwScore = Math.max(hwm.score, hwStale ? hwStale.score : 0);
      if (hwLevel > 0) {
        return {
          ...defaultResult,
          score: hwScore,
          level: hwLevel as TrustLevel,
          rawLevel: hwLevel as TrustLevel,
          postsRemainingToday: Infinity,
          loading: true,
        };
      }
    }
    // Truly new user or first visit — default to unrestricted while loading
    return { ...defaultResult, postsRemainingToday: Infinity };
  });

  // Track the highest level seen to prevent yo-yo downgrades.
  // Trust should only go UP during normal use — a downgrade is almost always
  // a transient data issue (relay returning partial results).
  // Persisted in localStorage so it survives page reloads and component remounts.
  const highWaterRef = useRef<{ level: TrustLevel; score: number }>({ level: 0, score: 0 });

  // Seed high-water mark from persisted storage + stale cache on first mount.
  // This runs synchronously on first render via a ref guard.
  const hwmSeeded = useRef(false);
  if (!hwmSeeded.current && pubkey && !isOp) {
    hwmSeeded.current = true;
    const hwm = getHighWaterMark(pubkey);
    const stale = getStaleCache(pubkey);
    const cacheLevel = stale ? stale.level : 0;
    const cacheScore = stale ? stale.score : 0;
    highWaterRef.current = {
      level: Math.max(hwm.level, cacheLevel) as TrustLevel,
      score: Math.max(hwm.score, cacheScore),
    };
  }

  // Seed the high water mark from fresh cache if available
  useEffect(() => {
    if (cached && !isOp && pubkey) {
      if (cached.level > highWaterRef.current.level || cached.score > highWaterRef.current.score) {
        highWaterRef.current = { level: cached.level, score: cached.score };
        saveHighWaterMark(pubkey, cached.level, cached.score);
      }
    }
  }, [cached, isOp, pubkey]);

  useEffect(() => {
    if (!pubkey) return;
    if (cached) {
      const effectiveLevel = isOp ? 3 as TrustLevel : cached.level;
      const effectiveRawLevel = isOp ? 3 as TrustLevel : cached.rawLevel;

      // Update high water mark (persisted)
      if (!isOp && pubkey && (effectiveLevel > highWaterRef.current.level || cached.score > highWaterRef.current.score)) {
        highWaterRef.current = { level: effectiveLevel, score: cached.score };
        saveHighWaterMark(pubkey, effectiveLevel, cached.score);
      }

      setResult({
        score: isOp ? 999 : cached.score,
        level: effectiveLevel,
        rawLevel: effectiveRawLevel,
        ageDays: isOp ? 999 : accountAgeDays(cached.firstSeen),
        firstSeen: cached.firstSeen,
        isVerified: isOp ? true : cached.signals.isVerified,
        isCapped: !isOp && effectiveLevel < effectiveRawLevel,
        postsRemainingToday: Infinity,
        loading: false,
        isOperatorAccount: isOp,
        signals: cached.signals,
      });
      return;
    }

    // ── Guard: don't recalculate with empty/sparse data if we know the user isn't new ──
    // When the cache expires and queries re-fire, relays return 0 events
    // initially before the real data arrives. Without this guard, the score
    // would temporarily drop to 0 ("New User") before bouncing back.
    //
    // We check multiple sources to determine if we should wait for more data:
    // 1. High-water mark (persisted in localStorage, survives remounts)
    // 2. Stale cache (expired but still has signal data)
    //
    // If any of these indicate the user previously had a non-zero score,
    // we wait for relay data before recalculating.
    const hwm = highWaterRef.current;
    const staleForGuard = getStaleCache(pubkey);
    const previouslyHadData = hwm.score > 0 || (staleForGuard && staleForGuard.score > 0);

    if (userPosts.length === 0 && previouslyHadData) {
      // Keep showing the previous result while relay data loads
      return;
    }

    // If no posts yet but user IS verified, still compute the score.
    const firstSeen = userPosts.length > 0 ? findOldestTimestamp(userPosts) : 0;
    const effectiveFirstSeen = firstSeen > 0 ? firstSeen : Math.floor(Date.now() / 1000);

    // Use the actual NIP-05 verification result (server-confirmed)
    const isVerified = nip05Verified;

    const replies = userPosts.filter(e => e.tags?.some((t: string[]) => t[0] === 'e'));
    const originals = userPosts.filter(e => !e.tags?.some((t: string[]) => t[0] === 'e'));

    const daySet = new Set<string>();
    for (const e of userPosts) {
      const d = new Date(e.created_at * 1000);
      daySet.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
    }

    const signals: TrustSignals = {
      isVerified,
      firstSeenTimestamp: effectiveFirstSeen,
      activeDays: daySet.size,
      postCount: originals.length,
      meaningfulReplyCount: replies.length,
      tipsSentCount: tipsSent.length,
      tipsReceivedCount: tipsReceived.length,
      crowdfundContributions: pledges.length,
      merchantReferrals: 0,
      isBchLocalMerchant: false,
      spamReportsReceived: 0,
      moderatorActions: 0,
      confirmedSpamCount: 0,
      duplicateAccountFlags: 0,
      botBehaviorFlags: 0,
    };

    const score = calculateTrustScore(signals);
    // Apply verification cap: unverified users are capped at Level 1
    const level = getTrustLevel(score, isVerified);
    const rawLevel = getRawTrustLevel(score);

    // ── High water mark: never downgrade level unless we have MORE data ──
    // If the newly calculated level is LOWER than what we've previously
    // shown, keep the higher level. This prevents the yo-yo effect where
    // partial relay data causes temporary downgrades.
    // Exception: if the new score is computed from MORE data (more total
    // events), trust it — it's a genuine recalculation with better data.
    let effectiveLevel = level;
    let effectiveScore = score;
    if (!isOp && level < highWaterRef.current.level) {
      // Check previous data volume from in-memory cache OR stale localStorage cache
      const prevSignals = _cache.get(pubkey)?.signals || getStaleCache(pubkey)?.signals;
      const prevTotalEvents = prevSignals
        ? prevSignals.postCount + prevSignals.meaningfulReplyCount + prevSignals.tipsSentCount + prevSignals.tipsReceivedCount
        : 0;
      const currTotalEvents = originals.length + replies.length + tipsSent.length + tipsReceived.length;

      // Only accept a downgrade if we have at least as much data as before.
      // Use a generous threshold (90%) to account for relay variability —
      // the old 80% was too lenient and allowed false downgrades.
      if (prevTotalEvents > 0 && currTotalEvents < prevTotalEvents * 0.9) {
        // We have less data than before — relay hasn't fully responded yet.
        // Keep the previous level to avoid flicker.
        effectiveLevel = highWaterRef.current.level;
        effectiveScore = highWaterRef.current.score;
      } else if (prevTotalEvents === 0 && highWaterRef.current.score > 0) {
        // No previous signal data at all but high-water mark exists.
        // This happens after a full cache clear / page refresh.
        // Be conservative: keep the high-water level unless the new data
        // is substantial enough to be trustworthy (at least 10 total events).
        if (currTotalEvents < 10) {
          effectiveLevel = highWaterRef.current.level;
          effectiveScore = highWaterRef.current.score;
        }
      }
    }

    // Update high water mark (persisted to localStorage)
    if (!isOp && (effectiveLevel > highWaterRef.current.level || effectiveScore > highWaterRef.current.score)) {
      highWaterRef.current = { level: effectiveLevel, score: effectiveScore };
      saveHighWaterMark(pubkey, effectiveLevel, effectiveScore);
    }

    setCache(pubkey, { score, level, rawLevel, firstSeen: effectiveFirstSeen, signals });

    setResult({
      score: isOp ? 999 : effectiveScore,
      level: isOp ? 3 : effectiveLevel,
      rawLevel: isOp ? 3 : rawLevel,
      ageDays: isOp ? 999 : accountAgeDays(effectiveFirstSeen),
      firstSeen: effectiveFirstSeen,
      isVerified: isOp ? true : isVerified,
      isCapped: !isOp && effectiveLevel < rawLevel,
      postsRemainingToday: Infinity,
      loading: false,
      isOperatorAccount: isOp,
      signals,
    });
  }, [pubkey, isOp, userPosts.length, tipsSent.length, tipsReceived.length,
      pledges.length, nip05Verified, cached]);

  return result;
}

